import { Button, Flex, Modal, Tooltip } from "antd";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import PermissionCheck from "~Common/PermissionCheck";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { GroupingBase } from "~pages/Standings/enums/groupingBase";
import type { GroupingConferenceDivision } from "~pages/Standings/enums/groupingConferenceDivision";
import type { GroupingStagePool } from "~pages/Standings/enums/groupingStagePool";
import { useFetchAllStandings } from "~pages/Standings/hooks/useFetchAllStandings";
import { useGetSeasonStandingConfiguration } from "~pages/Standings/hooks/useGetSeasonStandingConfiguration";
import { useStandingsService } from "~pages/Standings/hooks/useStandingsService";
import { AddStandingModal } from "~pages/Standings/StandingsListPage/components/AddStandingModal";
import { StandingsGroupingControls } from "~pages/Standings/StandingsListPage/components/StandingsGroupingControls";
import { useGetColumns } from "~pages/Standings/StandingsListPage/hooks/useGetColumns";
import { useStandingsTableData } from "~pages/Standings/StandingsListPage/hooks/useStandingsTableData";
import { getStandingGroupings } from "~pages/Standings/StandingsListPage/utilities/getStandingGroupings";
import type { StandingTransformed } from "~pages/Standings/types/standingTransformed";

const StandingsListPage = () => {
  const { data: standingConfiguration, isLoading: isStandingConfigurationLoading } = useGetSeasonStandingConfiguration();
  const { formatMessage } = useIntl();
  const { sport, organizationId, seasonId, customerId } = useParams();
  const navigate = useNavigate();
  const { uiConfiguration } = useSportConfiguration();
  const paginationConfig = useHandlePagination(10);
  const columns = useGetColumns();

  const standingsGrouping = getStandingGroupings(standingConfiguration);
  const [grouping, setGrouping] = useState<GroupingStagePool>();
  const [conferenceGrouping, setConferenceGrouping] = useState<GroupingConferenceDivision>();

  const params = {
    offset: "0",
    limit: "1000",
    groupingStagePool: grouping,
    groupingConferenceDivision: conferenceGrouping,
    groupingBase: GroupingBase.Overall,
    include: ["seasonStages", "seasonPools", "conferences", "divisions", "entities", "persons"].join(","),
  };

  const { data, isLoading } = useFetchAllStandings(
    params,
    !isStandingConfigurationLoading && !!grouping && !!conferenceGrouping,
    grouping,
    conferenceGrouping,
  );
  const { dataSource, tabs, parentSelect } = useStandingsTableData(data, grouping, conferenceGrouping);

  const { buildStandings, isBuildPending, createStanding, isCreatePending, deleteStandings, isDeleteStandingsPending } =
    useStandingsService();
  const [modal, contextHolder] = Modal.useModal();

  const handleDelete = () => {
    modal.confirm({
      title: formatMessage({ id: "standings.delete" }),
      content: formatMessage({ id: "standings.delete.confirm" }),
      okText: formatMessage({ id: "delete" }),
      okButtonProps: { loading: false },
      onOk: () => deleteStandings(),
    });
  };

  if (standingConfiguration && !grouping && !conferenceGrouping) {
    setGrouping(standingsGrouping.STAGE_POOL.defaultValue);
    setConferenceGrouping(standingsGrouping.CONFERENCE_DIVISION.defaultValue);
  }

  const loading = isStandingConfigurationLoading || isLoading || isBuildPending || isDeleteStandingsPending;

  return (
    <>
      {contextHolder}
      <DataTable<StandingTransformed>
        title={formatMessage({ id: "standings" })}
        headerCustomFilters={
          <StandingsGroupingControls
            standingsGrouping={standingsGrouping}
            grouping={grouping}
            conferenceGrouping={conferenceGrouping}
            setGrouping={setGrouping}
            setConferenceGrouping={setConferenceGrouping}
            parentSelect={parentSelect}
            disabled={loading}
          />
        }
        headerCustomActions={
          <Flex gap={10} align="center" wrap="wrap" justify="flex-end">
            <PermissionCheck permissionKeys={[PermissionNames.STANDING_ADJUSTMENT]}>
              <Tooltip title={formatMessage({ id: "standings.adjustments" })}>
                <Button
                  icon={<i className="fas fa-wrench" style={{ marginRight: "5px" }} />}
                  disabled={loading}
                  type="primary"
                  onClick={() => navigate(`/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/standings/adjustments`)}
                >
                  <FormattedMessage id="standings.adjustments" />
                </Button>
              </Tooltip>
            </PermissionCheck>
            <PermissionCheck permissionKeys={[PermissionNames.STANDING]} permissionOptions={[PermissionOperations.CREATE]}>
              <Button
                icon={<i className="fas fa-arrow-rotate-left" style={{ marginRight: "5px" }} />}
                type="primary"
                disabled={loading}
                onClick={() => buildStandings(standingConfiguration)}
              >
                <FormattedMessage id={"buildStandings"} />
              </Button>
              <AddStandingModal disabled={loading} createStanding={createStanding} isCreatePending={isCreatePending} />
              <PermissionCheck permissionKeys={[PermissionNames.STANDING]} permissionOptions={[PermissionOperations.DELETE]}>
                <Button
                  icon={<i className="fas fa-trash" style={{ marginRight: "5px" }} />}
                  type="primary"
                  disabled={loading}
                  onClick={handleDelete}
                >
                  <FormattedMessage id={"standings.delete"} />
                </Button>
              </PermissionCheck>
            </PermissionCheck>
          </Flex>
        }
        columns={columns}
        rowKey="standingId"
        dataSource={dataSource}
        tabsConfig={{ items: tabs }}
        isDataLoading={loading}
        columnChooserLocalStorageKey={`${sport}.tables.settings.columns.standings`}
        permissionNames={[PermissionNames.STANDING]}
        dataTableUiConfiguration={uiConfiguration.pages.standings.listPage}
        paginationConfig={{ ...paginationConfig, total: dataSource.length }}
      />
    </>
  );
};

export default StandingsListPage;
